/**
 * Contacts view component.
 * @module components/View/ContactsView
 */

import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Input,
  Label,
  Table,
  TableHeader,
  TableRow,
  TableBody,
  TableCell,
  TableHeaderCell,
} from 'semantic-ui-react';
import { Link, useHistory } from 'react-router-dom';

import { Icon } from '@plone/volto/components';
import { flattenToAppURL } from '@plone/volto/helpers';

import downSVG from '@plone/volto/icons/down-key.svg';
import upSVG from '@plone/volto/icons/up-key.svg';
import clearSVG from '@plone/volto/icons/clear.svg';
import addSVG from '@plone/volto/icons/add.svg';

/**
 * Contacts view component class.
 * @function ContactsView
 * @param {Object} content Content object.
 * @returns {string} Markup of the component.
 */
const ContactsView = ({ content }) => {
  const history = useHistory();
  let items = [...content.items];

  // Filtering
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [company, setCompany] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');

  const firstNameRef = useRef(null);
  const lastNameRef = useRef(null);
  const companyRef = useRef(null);
  const emailRef = useRef(null);

  const filterField = (items, field, value) =>
    value === ''
      ? items
      : (items = items.filter(
          (item) =>
            item[field] &&
            item[field].toLowerCase().indexOf(value.toLowerCase()) !== -1,
        ));

  items = filterField(items, 'first_name', firstName);
  items = filterField(items, 'last_name', lastName);
  items = filterField(items, 'company', company);
  items = filterField(items, 'email', email);
  items = filterField(items, 'phone', phone);

  // Sorting
  const [sort, setSort] = useState('last_name');
  const [dir, setDir] = useState('asc');

  items = items.sort((a, b) =>
    (dir === 'asc' ? a[sort] > b[sort] : a[sort] < b[sort]) ? 1 : -1,
  );

  const onSort = (value) => {
    if (sort === value) {
      setDir(dir === 'asc' ? 'desc' : 'asc');
    } else {
      setSort(value);
      setDir('asc');
    }
  };

  const SortIcon = () => (
    <Icon
      name={dir === 'desc' ? upSVG : downSVG}
      className="icon"
      size="20px"
    />
  );

  return (
    <article id="contacts">
      <div className="button-toolbar">
        <Link
          className="circular-button primary float-left"
          to={`${flattenToAppURL(content['@id'])}/add?type=Contact`}
        >
          <Icon name={addSVG} size="24px" />
        </Link>
        <div className="button-toolbar-title">
          Contacts <Label circular>{items.length}</Label>
        </div>
      </div>
      <div className="view-padding">
        <div className="scrollable-table">
          <Table striped celled selectable>
            <TableHeader>
              <TableRow className="tools-header">
                <TableHeaderCell onClick={() => onSort('first_name')} width="2">
                  First Name {sort === 'first_name' && <SortIcon />}
                </TableHeaderCell>
                <TableHeaderCell onClick={() => onSort('last_name')} width="2">
                  Last Name {sort === 'last_name' && <SortIcon />}
                </TableHeaderCell>
                <TableHeaderCell onClick={() => onSort('company')} width="4">
                  Company {sort === 'company' && <SortIcon />}
                </TableHeaderCell>
                <TableHeaderCell onClick={() => onSort('email')} width="4">
                  Email {sort === 'email' && <SortIcon />}
                </TableHeaderCell>
              </TableRow>
              <TableRow className="tools-filter">
                <TableHeaderCell>
                  <div className="filter-input">
                    <Input
                      value={firstName}
                      placeholder="Filter first name"
                      autoCorrect="off"
                      spellCheck="false"
                      ref={firstNameRef}
                      onChange={(e) => setFirstName(e.target.value)}
                    />
                    {firstName && (
                      <Icon
                        name={clearSVG}
                        size="16px"
                        className="clear-icon"
                        onClick={() => {
                          setFirstName('');
                          firstNameRef.current?.focus();
                        }}
                      />
                    )}
                  </div>
                </TableHeaderCell>
                <TableHeaderCell>
                  <div className="filter-input">
                    <Input
                      value={lastName}
                      placeholder="Filter last name"
                      autoCorrect="off"
                      spellCheck="false"
                      ref={lastNameRef}
                      onChange={(e) => setLastName(e.target.value)}
                    />
                    {lastName && (
                      <Icon
                        name={clearSVG}
                        size="16px"
                        className="clear-icon"
                        onClick={() => {
                          setLastName('');
                          lastNameRef.current?.focus();
                        }}
                      />
                    )}
                  </div>
                </TableHeaderCell>
                <TableHeaderCell>
                  <div className="filter-input">
                    <Input
                      value={company}
                      placeholder="Filter company"
                      autoCorrect="off"
                      spellCheck="false"
                      ref={companyRef}
                      onChange={(e) => setCompany(e.target.value)}
                    />
                    {company && (
                      <Icon
                        name={clearSVG}
                        size="16px"
                        className="clear-icon"
                        onClick={() => {
                          setCompany('');
                          companyRef.current?.focus();
                        }}
                      />
                    )}
                  </div>
                </TableHeaderCell>
                <TableHeaderCell>
                  <div className="filter-input">
                    <Input
                      value={email}
                      placeholder="Filter email"
                      autoCapitalize="none"
                      autoCorrect="off"
                      spellCheck="false"
                      ref={emailRef}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                    {email && (
                      <Icon
                        name={clearSVG}
                        size="16px"
                        className="clear-icon"
                        onClick={() => {
                          setEmail('');
                          emailRef.current?.focus();
                        }}
                      />
                    )}
                  </div>
                </TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <TableRow
                  className={item.id !== '000000' ? 'clickable-row' : ''}
                  onClick={
                    item.id !== '000000'
                      ? () => history.push(flattenToAppURL(item['@id']))
                      : undefined
                  }
                >
                  <TableCell>{item.first_name}</TableCell>
                  <TableCell>{item.last_name}</TableCell>
                  <TableCell>{item.company}</TableCell>
                  <TableCell>{item.email}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </article>
  );
};

/**
 * Property types.
 * @property {Object} propTypes Property types.
 * @static
 */
ContactsView.propTypes = {
  content: PropTypes.shape({
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    company: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
  }).isRequired,
};

export default ContactsView;
